import React, { useState } from 'react';
import Plot from 'react-plotly.js';
import { Row, Select } from 'antd';

const Simulations3DSurface = ({ simulations, assetNames }) => {
  const defaultAssets = assetNames.slice(0, 3);
  const [selectedAssets, setSelectedAssets] = useState(defaultAssets);

  if (!simulations || simulations.length === 0) {
    return <div>No simulation data available</div>;
  }

  const selectedIndices = selectedAssets.map(asset => assetNames.indexOf(asset));
  const zData = selectedIndices.map(index => simulations[index]);
  const xLabels = selectedAssets;
  const yLabels = Array.from({ length: zData.length }, (_, i) => ` ${assetNames[i + 1]}`);

  // Find peak points for the 3D line
  const findPeakPoints = () => {
    const peaks = [];
    for (let i = 0; i < zData.length; i++) {
      const maxInRow = Math.max(...zData[i]);
      const maxIndex = zData[i].indexOf(maxInRow);
      peaks.push({
        x: xLabels[maxIndex],
        y: yLabels[i],
        z: maxInRow
      });
    }
    return peaks;
  };

  const peakPoints = findPeakPoints();

  const handleSelectChange = (value) => {
    setSelectedAssets(prev => [...prev, value]);
  };

  return (
    <div className="w-full h-full">
      <Row justify={'center'}>
        <Select
          multiple
          className="w-[200px] mx-auto mb-5"
          placeholder="Select up to three assets to plot"
          value={selectedAssets}
          onChange={handleSelectChange}
        >
          {assetNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
      </Row>

      {selectedAssets.length > 0 && (
        <div className="rounded-xl w-full h-[80vh]">
          <Plot
            data={[
              // Main surface
              {
                z: zData,
                x: xLabels,
                y: yLabels,
                type: 'surface',
                colorscale: [
                  [0, '#0A2472'],    // Deep blue
                  [0.25, '#0D47A1'], // Royal blue
                  [0.5, '#1976D2'],  // Medium blue
                  [0.75, '#4FC3F7'], // Light blue
                  [1, '#B2EBF2']     // Cyan
                ],
                showscale: false,
                hoverinfo: 'x+y+z',
                hoverlabel: {
                  bgcolor: '#0A2472',
                  bordercolor: '#4FC3F7',
                  font: { color: '#B2EBF2' }
                },
                hovertemplate:
                  '<b>Assets:</b> %{x}<br>' +
                  '<b>Simulation:</b> %{y}<br>' +
                  '<b>Value:</b> %{z:.2f}<extra></extra>',
                lighting: {
                  ambient: 0.6,
                  diffuse: 0.8,
                  fresnel: 0.2,
                  specular: 1,
                  roughness: 0.5
                },
                contours: {
                  z: {
                    show: true,
                    usecolormap: true,
                    highlightcolor: "#4FC3F7",
                    project: { z: true }
                  }
                }
              },
              // Peak line
              {
                type: 'scatter3d',
                mode: 'lines+markers',
                x: peakPoints.map(p => p.x),
                y: peakPoints.map(p => p.y),
                z: peakPoints.map(p => p.z),
                line: {
                  color: '#80DEEA',
                  width: 6
                },
                marker: {
                  color: '#B2EBF2',
                  size: 4,
                  symbol: 'circle'
                },
                hoverinfo: 'z',
                hovertemplate: '<b>Peak Value:</b> %{z:.2f}<extra></extra>'
              }
            ]}
            layout={{
              autosize: true,
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              margin: {
                l: 0,
                r: 0,
                b: 0,
                t: 0,
                pad: 0
              },
              scene: {
                camera: {
                  eye: {
                    x: 1.87,
                    y: 0.88,
                    z: 0.64
                  },
                  center: { x: 0, y: 0, z: -0.1 },
                  up: { x: 0, y: 0, z: 1 }
                },
                xaxis: {
                  showgrid: true,
                  gridcolor: '#1976D2',
                  gridwidth: 1,
                  title: {
                    text: 'Asset Combinations',
                    font: { color: '#B2EBF2', size: 16 }
                  },
                  color: '#B2EBF2',
                  tickfont: { color: '#B2EBF2', size: 12 }
                },
                yaxis: {
                  showgrid: true,
                  gridcolor: '#1976D2',
                  gridwidth: 1,
                  title: {
                    text: 'Simulation Path',
                    font: { color: '#B2EBF2', size: 16 }
                  },
                  color: '#B2EBF2',
                  tickfont: { color: '#B2EBF2', size: 16 }
                },
                zaxis: {
                  showgrid: true,
                  gridcolor: '#1976D2',
                  gridwidth: 1,
                  title: {
                    text: 'Price Level',
                    font: { color: '#B2EBF2', size: 12 }
                  },
                  color: '#B2EBF2',
                  tickfont: { color: '#B2EBF2', size: 10 }
                },
                aspectratio: {
                  x: 1.75,
                  y: 1.75,
                  z: 1
                },
                bgcolor: 'transparent',

              }
            }}
            config={{
              displayModeBar: false,
              responsive: true,

              scrollZoom: true,
              showTips: true
            }}
            style={{
              width: '100%',
              height: '100%'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Simulations3DSurface;